/**
 * # Player
 *
 * This is the Animoto wrapper for JW Player.  It exposes the 'Player' Class
 *
 * @requires jwPlayer
 */
/*
  The JWPlayer SWF and Animoto css files for styling the player are required here
  so that they can be packaged and deployed properly by webpack
*/
require('file?name=jwplayer.flash.swf!../vendor/jwplayer.flash.swf');
require('./player.css');
/*
  The JWPlayer javascript is imported and assigned to the jwplayer variable
*/
import jwplayer from '../vendor/jwplayer.js';
import errorCodes from './errorCodes.js';
import { uuidV4 } from './helpers.js';

export default class Player {
  /**
   * Constructor for the Player class.  Takes in the element and options
   * and instantiates a JWPlayer object on that element
   *
   * @method constructor
   *
   * @param  {String}  playerElementId   The ID of the element on the page in which to instantiate the player
   * @param  {Object}  videoOptions      All of the options necessary to instantiate the player (playlist, cover image, etc.)
   *
   * @example
   *  var somePlayer = new Player('player-element-id', {
   *    playlist: [{
   *      image: 'https://s3.amazonaws.com/s3-p.animoto.com/Video/WMsbHDPVQetrRdVDhezlbg/cover_1920x1080.jpg',
   *      sources: [{
   *        file      : 'https://s3.amazonaws.com/s3-p.animoto.com/Video/9RXZ4u2JRm9pwGO9LtPccg/360p.mp4',
   *        label     : '1080p',
   *        'default' : true
   *      }]
   *    }],
   *    ctaSettings : {
   *      title : 'This is my CTA title',
   *      url   : 'http://www.animoto.com'
   *    },
   *    eventHandlers : {
   *      INITIALIZED : function(playerType) { console.log(playerType + ' was initialized'); },
   *      STARTED     : function(playerType) { console.log(playerType + ' started playing'); },
   *      COMPLETED   : function(playerType) { console.log(playerType + ' finished playing'); },
   *    },
   *    qosTracker : 'fake-domain.com/qos_trail.gif',
   *    repeat     : false
   *    autostart  : false,
   *    mute       : false
   *  });
   *
   */
  constructor(playerElementId, videoOptions) {
    const defaults = {
            aspectratio : '16:9',
            width       : '100%',
            key         : 'ZUq0YueMEA5ypaCmfAevnc4dEwcC0fpq4TfHT6XzYClhguqE',
            flashplayer : '//d150hyw1dtprld.cloudfront.net/player/jwplayer.flash.swf',
            preload     : 'none',
            skin        : {
              active   : '#1bcdcf',
              inactive : '#eeeeee'
            }
          },
          setupOptions = Object.assign({}, defaults, videoOptions);

    this._uuid = uuidV4();

    this._resetDefaultPlaybackQuality();

    this._originalSetupOpitions = setupOptions;
    this.playerElementId = playerElementId;

    this._setupPlayer(playerElementId, setupOptions);

    // This error handler handles the scenario where an old animoto mp4 video
    // render with bad enconding is loaded by JWPlayer. This will default the
    // provider to flash to put the player into a playable state
    this.player.on('error', (event) => {
      const errorCode = this._findErrorCodeByValue(event.message);
      if (errorCode === 'UNSUPPORTED_FILE_ENCODING' && this.getProviderName() === 'html5') {
        this._setupPlayer(this.playerElementId, Object.assign({}, this._originalSetupOpitions, { primary : 'flash' }));
        this.player.once('ready', () => { this.player.play(true); });
      }
    });
  }

  /**
   * Instantiates a JWPlayer instance on an element calls setup on that instance.
   * Can be called multiple times on a player instance
   *
   * @method _setupPlayer
   * @private
   *
   * @param  {String}  playerElementId   The ID of the element on the page in which to instantiate the player
   * @param  {Object}  setupOptions      All of the options necessary to instantiate the player (playlist, cover image, etc.)
   *
   * @example
   *   playerInstanceVariable._setupPlayer('player-element-id', {
   *    playlist: [{
   *      image: 'https://s3.amazonaws.com/s3-p.animoto.com/Video/WMsbHDPVQetrRdVDhezlbg/cover_1920x1080.jpg',
   *      sources: [{
   *        file      : 'https://s3.amazonaws.com/s3-p.animoto.com/Video/9RXZ4u2JRm9pwGO9LtPccg/360p.mp4',
   *        label     : '1080p',
   *        'default' : true
   *      }]
   *    }],
   *    ctaSettings : {
   *      title : 'This is my CTA title',
   *      url   : 'http://www.animoto.com'
   *    },
   *    eventHandlers : {
   *      INITIALIZED : function(playerType) { console.log(playerType + ' was initialized'); },
   *      STARTED     : function(playerType) { console.log(playerType + ' started playing'); },
   *      COMPLETED   : function(playerType) { console.log(playerType + ' finished playing'); },
   *    },
   *    qosTracker : 'fake-domain.com/qos_trail.gif',
   *    repeat     : false
   *    autostart  : false,
   *    mute       : false
   *  });
   */
  _setupPlayer(playerElementId, setupOptions) {
    if (this.player) {
      this.player.remove();
    }

    this.player = jwplayer(playerElementId);

    if (setupOptions.playlist) {
      this._sortPlaylistSources(setupOptions.playlist);
    }

    this.player.setup(setupOptions);

    if (setupOptions.eventHandlers) {
      this._bindHandlers(setupOptions.eventHandlers);
    }

    this.player.on('levelsChanged', (event) => {
      this._previousQualityLevelIndex = this._currentQualityLevelIndex;
      this._currentQualityLevelIndex = event.currentQuality;
    });

    this.player.on('ready', () => {
      const playlist = this.player.getPlaylist(),
            playlistSources = playlist[0] && playlist[0].sources;

      this._previousQualityLevelIndex = null;

      let currentQualityLevelIndex = this._currentQualityLevelIndex > -1 ? this._currentQualityLevelIndex : this.player.getCurrentQuality();
      if (currentQualityLevelIndex < 0) {
        currentQualityLevelIndex = playlistSources.findIndex((source) => source['default']);
      }
      this._currentQualityLevelIndex = Math.max(currentQualityLevelIndex, 0);
    });

    // Because all seeks cause a play event in jwplayer, checking state of player
    // and then forcing it to pause if it was paused before the seek
    this.player.on('seek', () => {
      this._maintainPauseAfterSeek(this.player.getState());
    });

    // If there is a CTA set on the video
    if (setupOptions.ctaSettings) {
      this._setupCTA(setupOptions.ctaSettings);
    }

    if (setupOptions.qosTracker) {
      this._setupQOSBindings(setupOptions.qosTracker);
    }
  }
  /**
   * Sort playlist sources by label so that player HD toggle active and inactive styles
   * are set properly depending on playlist source length
   *
   * @method _sortPlaylistSources
   * @private
   *
   * @param  {Array} playlist Playist array with playlist items
   *
   * @example
   *   this._sortPlaylistSources([{
         image: 'https://s3.amazonaws.com/s3-p.animoto.com/Video/WMsbHDPVQetrRdVDhezlbg/cover_1920x1080.jpg',
         sources: [
          {
            file      : 'https://s3.amazonaws.com/s3-p.animoto.com/Video/9RXZ4u2JRm9pwGO9LtPccg/360p.mp4?t=720',
            label     : '720p'
          },
          {
            file      : 'https://s3.amazonaws.com/s3-p.animoto.com/Video/9RXZ4u2JRm9pwGO9LtPccg/360p.mp4',
            label     : '360p'
          },
          {
           file      : 'https://s3.amazonaws.com/s3-p.animoto.com/Video/9RXZ4u2JRm9pwGO9LtPccg/360p.mp4?t=480',
           label     : '480p',
           'default' : true
         }
       ]);
   */
  _sortPlaylistSources(playlist) {
    for (const item of playlist) {
      const sources = item.sources;

      if (sources.length > 2) {
        sources.sort((a, b) => {
          return -a.label.localeCompare(b.label);
        });
      } else {
        sources.sort((a, b) => {
          return a.label.localeCompare(b.label);
        });
      }
    }
  }

  /**
   * Set the volume on the wrapped player instance
   *
   * @method setVolume
   *
   * @param  {Integer} volume      The volume level on a 0 - 100 scale
   *
   * @example
   *   playerInstanceVariable.setVolume(20);
   */
  setVolume(volume) {
    this.player.setVolume(volume);
  }

  /**
   * Get the type of player being used (flash, html5, etc.)
   *
   * @method getProviderName
   *
   * @returns {String}  The type of provider currently being used for playback (html5, flash, etc.)
   *
   * @example
   *   var playerType = playerInstanceVariable.getProviderName();
   */
  getProviderName() {
    const provider = this.player.getProvider();
    return provider ? provider.name : '';
  }

  /**
   * Given an error message from JWPlayer returns an error code key
   *
   * @method _findErrorCodeByValue
   * @private
   *
   * @param  {String} value Error message given by JWPlayer
   *
   * @returns {String} Key that maps to the value passed to function
   *
   * @example
   *   this._findErrorCodeByValue('Error loading player: Flash version must be 11.2 or greater');
   */
  _findErrorCodeByValue(value) {
    return Object.keys(errorCodes).find(key => errorCodes[key] === value);
  }

  /**
   * Take the provided event handlers and bind them to the appropriate events triggered by JW Player
   * The event handler is always passed the value of getProviderName
   *
   * @method _bindHandlers
   * @private
   *
   * @param  {Object} eventHandlers    An object where the keys are possible events, and the values are the callback
   *
   * @example
   *   this._bindHandlers({ INITIALIZED : function(playerType) { console.log('Player ready', playerType); } });
   */
  _bindHandlers(eventHandlers) {
    // Loop through the properties of the object
    for (const k in eventHandlers) {
      if (eventHandlers.hasOwnProperty(k)) {
        let eventType,
            eventHandler;

        switch (k) {
          case 'INITIALIZED':
            eventType = 'ready';
            eventHandler = this._bindHandler(eventHandlers[k]);
            break;
          case 'STARTED':
            eventType = 'play';
            eventHandler = this._bindHandler(eventHandlers[k]);
            break;
          case 'COMPLETED':
            eventType = 'complete';
            eventHandler = this._bindHandler(eventHandlers[k]);
            break;
          case 'SETUP_ERROR':
            eventType = 'setupError';
            eventHandler = this._bindErrorHandler(eventHandlers[k]);
            break;
          case 'ERROR':
            eventType = 'error';
            eventHandler = this._bindErrorHandler(eventHandlers[k]);
            break;
          case 'PLAYLIST_LOADED':
            eventType = 'playlist';
            eventHandler = this._bindHandler(eventHandlers[k]);
            break;
          default:
            eventType = false;
        }

        if (eventType) {
          this.player.on(eventType, eventHandler);
        }
      }
    }
  }

  /**
   * Recieves a function and binds this classes scope to it
   * The event handler is always passed the value of getProviderName
   *
   * @method _bindHandler
   * @private
   *
   * @param {Function} handler A function that is an event handler
   *
   * @returns {Function} The passed in function wrapped in a function giving it this classes scope
   *
   * @example
   *   this._bindHandler(function(playerType) { console.log('Player ready', playerType); });
   */
  _bindHandler(handler) {
    return (...args) => {
      // Call the event handler with the player 'type'
      handler(this.getProviderName(), ...args);
    };
  }

  /**
   * Recieves a function and binds this classes scope to it
   * The event handler is always passed the value of getProviderName
   * and the error code from errorCodes map
   *
   * @method _bindErrorHandler
   * @private
   *
   * @param {Function} handler A function that is an event handler
   *
   * @returns {Function} The passed in function wrapped in a function giving it this classes scope
   *
   * @example
   *   this._bindErrorHandler(function(playerType, event, errorCode) { console.log('Player ready', playerType); });
   */
  _bindErrorHandler(handler) {
    return (event) => {
      const errorCode = this._findErrorCodeByValue(event.message);
      // Call the event handler with the player 'type' and error code
      handler(this.getProviderName(), event, errorCode);
    };
  }

  /**
   * Loads a new playlist into an existing Player instance. Also removes current
   * default playback quality so that the "default" flag playlist source item
   * is honored
   *
   * @method loadPlaylist
   * @public
   *
   * @param {Array} playlist Loads a new playlist into an existing Player instance
   *
   * @example
   *   playerInstanceVariable.loadPlaylist([{
   *   image: 'https://s3.amazonaws.com/s3-p.animoto.com/Video/WMsbHDPVQetrRdVDhezlbg/cover_1920x1080.jpg',
   *   sources: [
   *     {
   *       file      : 'https://s3.amazonaws.com/s3-p.animoto.com/Video/9RXZ4u2JRm9pwGO9LtPccg/720p.mp4',
   *       label     : '720p',
   *       'default' : true
   *     },
   *     {
   *      file      : 'https://s3.amazonaws.com/s3-p.animoto.com/Video/9RXZ4u2JRm9pwGO9LtPccg/360p.mp4',
   *      label     : '360p'
   *     }
   *    ]
   *  }]);
   */
  loadPlaylist(playlist) {
    this._resetDefaultPlaybackQuality();
    this._sortPlaylistSources(playlist);
    this.player.load(playlist);
    this._hideCTA();
  }

  /**
   * Returns the label of the current playback quality level
   *
   * @method getCurrentPlaybackQualityLabel
   * @public
   *
   * @returns {String} Current playback quality level
   *
   * @example
   *   this.getCurrentPlaybackQualityLabel();
   */
  getCurrentPlaybackQualityLabel() {
    const qualityLevels = this.player.getQualityLevels(),
          currentQualityLevelIndex = this._currentQualityLevelIndex;

    return qualityLevels.length > 0 && currentQualityLevelIndex >= 0 ? qualityLevels[currentQualityLevelIndex].label : null;
  }

  /**
   * Clears jwplayer localStorage variable which determines playback quality
   *
   * @method _resetDefaultPlaybackQuality
   * @private
   *
   * @example
   *   this._resetDefaultPlaybackQuality();
   */
  _resetDefaultPlaybackQuality() {
    localStorage.removeItem('jwplayer.qualityLabel');
  }

  /**
   * Sets up a one time event listener for the "seeked" event to pause
   * the player if it was paused before seeking
   *
   * @method _maintainPauseAfterSeek
   * @private
   *
   * @param {String} playState The play state on seeking
   *
   * @example
   *   this._maintainPauseAfterSeek('paused');
   */
  _maintainPauseAfterSeek(playState) {
    if (playState === 'paused') {
      this.player.once('seeked', () => {
        this.player.pause();
      });
    }
  }

  /**
   * Seeks player to the point it was at before loading new playlist
   *
   * @method keepPlayPositionAfterPlaylistLoad
   * @public
   *
   * @param {String} preLoadPlayState The play state before loading playlist
   *
   * @example
   *   this.keepPlayPositionAfterPlaylistLoad('paused');
   */
  keepPlayPositionAfterPlaylistLoad(preLoadPlayState) {
    const position = this.player.getPosition();

    this.player.once('playlistItem', () => {
      this._maintainPauseAfterSeek(preLoadPlayState);
      if (position > 0) {
        this.player.seek(position);
      }
    });
  }

  /* ***********************************************************************************************
   *  CTA Related Functionality
   ** **********************************************************************************************/
  /**
   * Setup all of the CTA functionality
   *
   * @method _setupCTA
   * @private
   *
   * @param  {Object} ctaSettings    An object containing the cta label and url
   *
   * @example
   *   this._setupCTA({ title : 'CTA Title', url : 'www.google.com' });
   */
  _setupCTA(ctaSettings) {
    this._createCTA(ctaSettings);
    this.player.on('play', () => {
      this._hideCTA();
    });
    this.player.on('pause', () => {
      this._showCTA();
    });
    this.player.on('complete', () => {
      this._showCTA();
    });
  }

  /**
   * Take the CTA settings and create the appropriate HTML element, storing it on the class instance
   *
   * @method _createCTA
   * @private
   *
   * @param  {Object} ctaSettings    An object containing the cta label and url
   *
   * @example
   *   this._createCTA({ title : 'CTA Title', url : 'www.google.com' });
   */
  _createCTA(ctaSettings) {
    let url = ctaSettings.url;

    // Ensure that if the CTA didn't have a protocol, to default to basic http
    if ((url.indexOf('://') === -1) && (url.toLowerCase().indexOf('mailto:') === -1)) {
      url = 'http://' + url;
    }

    this.ctaElement = document.createElement('a');
    this.ctaElement.innerHTML = ctaSettings.title;
    this.ctaElement.setAttribute('href', url);
    this.ctaElement.setAttribute('target', '_blank');
    this.ctaElement.className = 'player_cta';
  }

  /**
   * Append the CTA element to the DOM to make it visible
   *
   * @method _showCTA
   * @private
   *
   * @example
   *   this._showCTA();
   */
  _showCTA() {
    if (this.ctaElement && !this.ctaElement.parentNode) {
      document.getElementById(this.playerElementId).appendChild(this.ctaElement);
    }
  }

  /**
   * Remove the CTA element from the DOM
   *
   * @method _hideCTA
   * @private
   *
   * @example
   *   this._hideCTA();
   */
  _hideCTA() {
    if (this.ctaElement && this.ctaElement.parentNode) {
      this.ctaElement.parentNode.removeChild(this.ctaElement);
    }
  }

  /* ***********************************************************************************************
   *  Quality of Service Tracking Related Functionality
   ** **********************************************************************************************/

  /**
   * @method _setupQOSBindings
   * @private
   *
   * @param  {String} qosTracker    The url of the tracking pixel to insert into the page
   *
   * @example
   *   this._setupQOSBindings('https://animoto.com/trail.gif');
   */
  _setupQOSBindings(qosTracker) {
    this.player.on('buffer', (details) => {
      this._loadCustomPixel(this._trackingUrl(qosTracker, 'buffering', {
        ps : details.oldstate,
        cs : details.newstate,
        r  : details.reason
      }));
    });
    this.player.once('firstFrame', (details) => {
      this._loadCustomPixel(this._trackingUrl(qosTracker, 'firstFrame', {
        ttff : details.loadTime
      }));
    });
    this.player.on('levelsChanged', (event) => {
      const qualityLevels = this.player.getQualityLevels(),
            oldQuality = qualityLevels.length > 0 && qualityLevels[this._previousQualityLevelIndex],
            newQuality = qualityLevels.length > 0 && qualityLevels[event.currentQuality];

      this._loadCustomPixel(this._trackingUrl(qosTracker, 'levelsChanged', {
        pq : oldQuality && oldQuality.label,
        cq : newQuality && newQuality.label
      }));
    });
  }

  /**
   * Inserts an image into the page to allow for tracking.  The CSS ensures the pixel isn't displayed
   *
   * @method _loadCustomPixel
   * @private
   *
   * @param  {String} url    The url of the tracking pixel to insert into the page
   *
   * @example
   *   this._loadCustomPixel('https://animoto.com/trail.gif?tracking=thing');
   */
  _loadCustomPixel(url) {
    const pixel = document.createElement('img');
    pixel.className = 'player_qos_tracking_pixel';
    pixel.setAttribute('src', url);
    pixel.setAttribute('alt', '');
    document.getElementById(this.playerElementId).appendChild(pixel);
  }

  /**
   * Returns okey of a file given a video or stream file url
   *
   * @method _getOkeyFromFileURL
   * @private
   *
   * @param  {String} fileUrl File url of a video or stream
   *
   * @returns {String} The okey contained in file url
   *
   * @example
   *   var okey = this._getOkeyFromFileURL('https://s3.amazonaws.com/s3-s.animoto.com/Stream/MzUCXRIc6cAAq27QPu2Chg/hls.360p15.m3u8');
   */
  _getOkeyFromFileURL(fileUrl) {
    const regEx = /\/(?:Video|Stream)\/(.+)\//,
          matches = regEx.exec(fileUrl);

    return matches.length > 1 ? matches[1] : '';
  }

  /**
   * Forms the url of the pixel to load to track all of the data for the fired event
   *
   * @method _trackingUrl
   * @private
   *
   * @param  {String} qosTracker      The url of the tracking pixel to insert into the page
   * @param  {String} eventType       The type of event that is being tracked
   * @param  {Object} additionalData  Any additional data specific to this event provided by the handler
   *
   * @returns {String}  The generated url including all of the data to be tracked
   *
   * @example
   *   var trackUrl = this._trackingUrl('https://animoto.com/trail.gif', 'start', {});
   */
  _trackingUrl(qosTracker, eventType, additionalData) {
    const sources = this.player.getPlaylist()[0].sources,
          currentPlaybackFile = sources[this._currentQualityLevelIndex].file;

    let url     = '//' + qosTracker;

    url += '?s=' + 'player_qos_v1' + // scope
      '&e=' + eventType + // event
      '&src=' + document.location.hostname + // source
      '&pt=' + this.getProviderName() + // player type
      '&t=' + Math.round((new Date()).getTime() / 1000) + // time of event
      '&file=' + encodeURIComponent(currentPlaybackFile) + // current file
      '&okey=' + this._getOkeyFromFileURL(currentPlaybackFile) + // current okey
      '&res=' + this.getCurrentPlaybackQualityLabel() + // current resolution
      '&p=' + this.player.getPosition() + // playhead position
      '&d=' + this.player.getDuration() +
      '&uuid=' + this._uuid;

    if (additionalData) {
      for (const k in additionalData) {
        if (additionalData.hasOwnProperty(k)) {
          url += '&' + encodeURIComponent(k) + '=' + encodeURIComponent(additionalData[k]);
        }
      }
    }

    return url;
  }

  /**
   * Removes JPlayer instance and CTA DOM
   *
   * @method destroy
   * @public
   *
   * @example
   *   playerInstance.destroy();
   */
  destroy() {
    this._hideCTA();
    this.player.remove();
  }

}



/** WEBPACK FOOTER **
 ** ./src/player.js
 **/